import React, { memo } from 'react';
import { useLanguage } from '../../contexts';

/**
 * PageContainer - A standalone page wrapper with gradient background and padding
 * 
 * **Important:** This is a complete layout component that should NOT be used inside AppLayout.
 * Use either PageContainer OR AppLayout, never both together. PageContainer provides its own
 * padding and overflow handling.
 * 
 * @example
 * <PageContainer gradient="cool">
 *   <YourPageContent />
 * </PageContainer>
 * 
 * @see AppLayout for pages that need header and navigation
 */

export type GradientType = 'warm' | 'cool' | 'neutral' | 'special' | 'none';

export interface PageContainerProps {
  children: React.ReactNode;
  /** Gradient preset for background */
  gradient?: GradientType;
  /** Custom background classes (overrides gradient) */
  bgClassName?: string;
  /** Whether to center content vertically */
  centerContent?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Max width constraint */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  /** Override text direction (defaults to language-based) */
  dir?: 'ltr' | 'rtl';
}

const gradientStyles: Record<GradientType, string> = {
  warm: 'bg-gradient-warm dark:bg-gradient-to-b dark:from-dark-bg dark:via-slate-900 dark:to-slate-800',
  cool: 'bg-gradient-cool dark:bg-gradient-to-b dark:from-dark-bg dark:via-slate-900 dark:to-slate-800',
  neutral: 'bg-gradient-neutral dark:bg-gradient-to-b dark:from-dark-bg dark:via-slate-900 dark:to-slate-800',
  special: 'bg-gradient-special dark:bg-gradient-to-b dark:from-dark-bg dark:via-slate-900 dark:to-slate-800',
  none: 'bg-white dark:bg-dark-bg',
};

const maxWidthStyles: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-full',
};

export const PageContainer: React.FC<PageContainerProps> = memo(({
  children,
  gradient = 'neutral',
  bgClassName,
  centerContent = false,
  className = '',
  maxWidth,
  dir,
}) => {
  const { isRTL } = useLanguage();
  
  const bgClasses = bgClassName || gradientStyles[gradient];
  const resolvedDir = dir ?? (isRTL ? 'rtl' : 'ltr');
  
  return (
    <div
      dir={resolvedDir}
      className={`
        h-dvh overflow-hidden
        transition-colors duration-300
        ${bgClasses}
        ${className}
      `}
    >
      <div
        className={`
          h-full overflow-y-auto
          px-4 py-6 sm:px-6 sm:py-8 pb-safe-bottom
          ${centerContent ? 'flex flex-col items-center justify-center' : ''}
          ${maxWidth ? `mx-auto ${maxWidthStyles[maxWidth]}` : ''}
        `}
      >
        {children}
      </div>
    </div>
  );
});

PageContainer.displayName = 'PageContainer';
